"use client";

import { useEffect } from "react";

import { useToast } from "@/src/hooks/use-toast";
import { ROOT_DOMAIN } from "@/src/lib/preview-env";

type Notice = { title: string; description: string };

/** The shared notice for a host that is itself the old address. */
function movedNotice(from: string, to: string): Notice {
  return {
    title: "This site has a new address",
    description: `${from}.${ROOT_DOMAIN} is moving to ${to}.${ROOT_DOMAIN} - please update any bookmarks or links accordingly. Thank you!`,
  };
}

// Which hosts show a notice. Heartland's (`ushv`, `ushv-heartland`) has been retired.
// TODO: Anyone reading this after October 21, 2026 - please remove LegacyHostNotice altogether
const NOTICE_BY_SUBDOMAIN: Record<string, Notice> = {
  "heartplace": movedNotice("heartplace", "ushv-heartplace"),
  "ushv-heartplace": movedNotice("heartplace", "ushv-heartplace"),
};

// Caps the notice at once per tab, cleared on logout
const NOTICE_SHOWN_SESSION_KEY = "vh_legacy_host_notice";

export function clearLegacyHostNotice(): void {
  sessionStorage.removeItem(NOTICE_SHOWN_SESSION_KEY);
}

/**
 * Tells members of a renamed tenant that its address changed, so bookmarks and saved links get
 * updated. Mounted on the login page and the dashboard layout, so it reaches members arriving on a
 * stale bookmark whether or not they still have a session. Renders nothing itself.
 */
export function LegacyHostNotice() {
  const { toast } = useToast();

  useEffect(() => {
    const subdomain = window.location.hostname.split(".")[0];
    const notice = subdomain ? NOTICE_BY_SUBDOMAIN[subdomain] : undefined;

    if (!notice || sessionStorage.getItem(NOTICE_SHOWN_SESSION_KEY) !== null) {
      return;
    }

    sessionStorage.setItem(NOTICE_SHOWN_SESSION_KEY, "shown");

    toast({
      title: notice.title,
      description: notice.description,
      duration: 10000, // Radix defaults to 5s, which is short for an address this long.
    });
  }, [toast]);

  return null;
}
